import BaseStepHandler from './BaseStepHandler';
import { Image, filters } from 'fabric';

/**
 * Handler for effects step type
 * Modified to render effects on top of all canvas elements
 */
export default class EffectsStepHandler extends BaseStepHandler {
    /**
     * Initialize effects
     */
    constructor(stepId, stepDefinition, canvas, stepManager, side) {
        super(stepId, stepDefinition, canvas, stepManager, side);
        
        // Store a reference to the effect element
        this.effectElement = null;
        this.effectElementId = `${this.stepId}_effect`;
        
        // Store the chosen effect
        this.currentEffect = null;
    }
    
    /**
     * Initialize effects
     */
    initialize() {
        super.initialize();
        
        // No specific initialization needed for effects
        // The element will be created when an effect is selected
        return this;
    }
    
    /**
     * Process template when no data exists
     */
    processTemplate() {
        // Get step data
        const stepData = this.getStepData();
        
        // If we have data, update with it
        if (stepData && Object.keys(stepData).length > 0) {
            this.update(stepData);
        } else {
            // Otherwise remove any existing effects
            this.removeEffect();
        }
        
        return this;
    }

    /**
     * Activate effects step
     */
    activate() {

        console.log('Doing activate on effect');
        super.activate();
        
        return this;
    }
    
    /**
     * Deactivate effects step
     */
    deactivate() {

        super.deactivate();
        this.removeEffect();
        this.currentEffect = null;

        return this;
    }
    
    /**
     * Update effects based on step data
     * 
     * @param {object} stepData - Step data
     */
    update(stepData) {
        super.update(stepData);
        
        // If disabled or no selection, remove effect
        if (!stepData || !stepData.enabled || !stepData.selected) {
            this.removeEffect();
            return this;
        }
        
        // Apply effect as an overlay
        this.applyEffect(stepData.selected);
        
        return this;
    }
    
    /**
     * Apply effect as an overlay on top of all elements
     * 
     * @param {object} selectedEffect - Selected effect data
     */
    applyEffect(selectedEffect) {
        // Store the currently selected effect
        this.currentEffect = selectedEffect;
        
        // Get effect image path
        const effectSrc = `/wp-content/plugins/octo-stc/public/assets/${selectedEffect.src}`;
        
        // Check if effect element already exists
        if (this.effectElement) {
            // Update existing effect element
            this.effectElement.setSrc(effectSrc).then(() => {
                this.canvas.canvas.requestRenderAll();
            });
            return;
        }
        
        // Create new effect element
        this.createEffectElement(effectSrc);
    }
    
    /**
     * Create a new effect element that covers the entire canvas
     * 
     * @param {string} effectSrc - Effect image source
     */
    createEffectElement(effectSrc) {

        if( this.effectElement || true ) return;

        Image.fromURL(effectSrc).then((img) => {
            
            if (!img) return;

            console.trace('Rendering effect');
            
            // Calculate effect size to cover the entire canvas
            const canvasWidth = this.canvas.canvas.width;
            const canvasHeight = this.canvas.canvas.height;
            
            // Configure the effect image
            img.set({
                name: this.effectElementId,
                left: canvasWidth / 2,
                top: canvasHeight / 2,
                originX: 'center',
                originY: 'center',
                width: canvasWidth,
                height: canvasHeight,
                scaleX: 1,
                scaleY: 1,
                opacity: 0.7, // Slightly transparent to show elements underneath
                selectable: this.isActive,
                hasControls: this.isActive && (this.definition.allowedActions?.includes('scale') ?? false),
                lockMovementX: !(this.isActive && (this.definition.allowedActions?.includes('move') ?? false)),
                lockMovementY: !(this.isActive && (this.definition.allowedActions?.includes('move') ?? false)),
                evented: this.isActive
            });
            
            // Set the blend mode
            img.globalCompositeOperation = 'multiply'; // Or other blend mode: overlay, screen, etc.
            
            // Store reference to effect element
            this.effectElement = img;
            this.elements.set(this.effectElementId, img);
            this.canvas.elementRefs.set(this.effectElementId, img);
            
            // Add to canvas with highest z-index
            this.canvas.canvas.add(img);
            
            // Move to top of stack to render above all other elements
            // img.bringToFront();
            
            // Render changes
            this.canvas.canvas.requestRenderAll();
            
            // Add event listeners for modifications
            this.attachModificationListener(img);
        });
    }
    
    /**
     * Attach modification listener to effect element
     * 
     * @param {object} element - Fabric element
     */
    attachModificationListener(element) {
        element.on('modified', () => {
            // Update step data with current position and scale
            const stepData = this.getStepData() || {};
            
            stepData.left = element.left;
            stepData.top = element.top;
            stepData.scaleX = element.scaleX;
            stepData.scaleY = element.scaleY;
            
            // Update step data without triggering another update
            this.setStepData(stepData, false);
        });
    }
    
    /**
     * Remove effect from canvas
     */
    removeEffect() {

        if (this.effectElement) {
            // Remove from canvas
            this.canvas.canvas.remove(this.effectElement);
            
            // Clear references
            this.canvas.elementRefs.delete(this.effectElementId);
            this.elements.delete(this.effectElementId);
            this.effectElement = null;
            
            // Render changes
            this.canvas.canvas.requestRenderAll();

        }
    }
    
    /**
     * Handle side change
     * @param {boolean} isCurrentSide - Whether this handler's side is now active
     */
    handleSideChange(isCurrentSide) {
        return this;
    }
    
    /**
     * Show effect element
     */
    show() {

        if(  !this.effectElement ) return  this;

        this.effectElement.set('visible', false);
        return this;

        if (this.effectElement) {
            this.effectElement.set('visible', true);
            this.isVisible = true;
            
            // Ensure effect is on top
            // this.effectElement.bringToFront();
            this.canvas.canvas.requestRenderAll();
        }
        return this;
    }
    
    /**
     * Hide effect element
     */
    hide() {
        if (this.effectElement) {
            this.effectElement.set('visible', false);
            this.isVisible = false;
            this.canvas.canvas.requestRenderAll();
        }
        
        return this;
    }
    
    /**
     * Render panel content
     */
    renderPanelContent() {
        const stepData = this.getStepData();
        const price = this.definition.price || 0;
        const options = this.definition.options || [];
        
        return `
            <div class="stc-select-field">
                <div class="stc-select-header">
                    <label class="stc-switch">
                        <input type="checkbox" 
                                class="stc-select-toggle"
                                ${stepData?.enabled ? 'checked' : ''} 
                                data-price="${price}">
                        <span class="stc-slider"></span>
                    </label>
                    <span class="stc-toggle-label">
                        ${this.definition.title}
                        ${price ? `<span class="stc-price">(+$${price})</span>` : ''}
                    </span>
                </div>
    
                <div class="stc-select-options ${stepData?.enabled ? 'is-enabled' : ''}">
                    ${options.map(option => `
                        <div class="stc-select-option ${stepData?.selected?.id === option.id ? 'is-selected' : ''}" 
                                data-option-id="${option.id}">
                            <div class="stc-select-option-image">
                                <img src="/wp-content/plugins/octo-stc/public/assets/${option.src}" alt="${option.title}">
                            </div>
                            <div class="stc-select-option-title">${option.title}</div>
                        </div>
                    `).join('')}
                </div>
            </div>
        `;
    }
    
    /**
     * Bind panel events
     */
    bindPanelEvents() {
        if (!this.panelElement) return;
        
        // Get UI elements
        const toggleSwitch = this.panelElement.querySelector('.stc-select-toggle');
        const optionsContainer = this.panelElement.querySelector('.stc-select-options');
        const options = this.panelElement.querySelectorAll('.stc-select-option');
        const clearButton = this.panelElement.querySelector('.stc-clear-step');
        
        // Bind toggle switch
        if (toggleSwitch) {
            this.addEventListener(toggleSwitch, 'change', () => {
                const stepData = this.getStepData() || {};
                stepData.enabled = toggleSwitch.checked;
                
                // Toggle options container
                if (optionsContainer) {
                    optionsContainer.classList.toggle('is-enabled', stepData.enabled);
                }
                
                // Update clear button
                if (clearButton) {
                    clearButton.disabled = !stepData.enabled;
                }
                
                this.setStepData(stepData);
            });
        }
        
        // Bind option selection
        options.forEach(option => {
            this.addEventListener(option, 'click', () => {
                // Get option data
                const optionId = option.dataset.optionId;
                if (!optionId) return;
                
                // Update selection in UI
                options.forEach(opt => {
                    opt.classList.toggle('is-selected', opt.dataset.optionId === optionId);
                });
                
                // Update step data
                const stepData = this.getStepData() || { enabled: true };
                const selectedOption = this.definition.options.find(opt => opt.id === optionId);
                stepData.selected = selectedOption;
                
                this.setStepData(stepData);
            });
        });
        
        // Bind clear button
        if (clearButton) {
            this.addEventListener(clearButton, 'click', () => {
                this.clear();
            });
        }
    }
    
    /**
     * Step-specific reset logic
     */
    onReset() {
        // Remove effect from canvas
        this.removeEffect();
        
        // Reset current effect
        this.currentEffect = null;
    }

    /**
     * Get export data for effects step
     * 
     * @returns {object} Export data
     */
    getStepExportData() {
        // Get base export data
        const exportData = super.getStepExportData();
        const stepData = this.getStepData();
        
        // Add effects-specific data
        exportData.effectsContent = {
            enabled: stepData?.enabled === true,
            
            // Which effect is selected
            selectedEffect: stepData?.selected ? {
                id: stepData.selected.id,
                title: stepData.selected.title,
                src: stepData.selected.src
            } : null,
            
            // Position data if available
            position: this.effectElement ? {
                left: this.effectElement.left,
                top: this.effectElement.top,
                scaleX: this.effectElement.scaleX,
                scaleY: this.effectElement.scaleY,
                angle: this.effectElement.angle || 0,
                opacity: this.effectElement.opacity || 0.7
            } : null,
            
            // Effect reference data
            effectData: stepData?.selected ? {
                type: 'effect',
                src: stepData.selected.src,
                needsProcessing: false,
                referenceOnly: true // Just a reference to a predefined effect
            } : null,
            
            // Processing instructions for production
            processingInstructions: {
                type: 'effect_overlay', // Changed to indicate this is an overlay effect
                blendMode: 'multiply',  // Default blend mode
                opacity: 0.7,
                applyAsOverlay: true    // Flag to indicate this should be applied as an overlay
            }
        };
        
        return exportData;
    }
    
    /**
     * Clean up resources
     */
    dispose() {
        // Remove effect element
        this.removeEffect();
        
        // Call parent dispose
        super.dispose();
    }

    /**
     * Prepare handler for preview generation
     * Called before high-resolution preview is generated
     * @returns {Promise<void>}
     */
    async prepareForPreview() {
        this.hide();
    }

}